import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import cn from 'classnames';

import Collapse from 'react-tiny-collapse';

import useClickOutside from 'hooks/use-click-outside';
import useEscape from 'hooks/use-escape';

import Search from 'components/search';
import HeaderToggleButton from './header-toggle-button';
import MenuHeaders from 'components/menu-headers';
import Logo from 'components/logo';
import Menu from 'components/menu';
import TabTrapper from 'components/tab-trapper';
import LoginLink from 'components/login-link';
import GlobalWarningBlock from 'components/global-warning-block';
import HousingProjectMenu from 'components/housing-project-menu';
import useStickyMenu from 'hooks/use-sticky-menu';

const Header = ({
  menuToggleLabel,
  searchToggleLabel,
  searchToggleText,
  linkToHomeUrl,
  linkToHomeText,
  menu,
  search,
  housingProjectMenu,
  globalWarningBlock
}) => {
  const [currentActiveGroup, setCurrentActiveGroup] = useState('');
  const [searchIsOpen, setSearchIsOpen] = useState(false);

  const headerRef = useRef(null);
  const timeoutRef = useRef(null);

  const {
    calculateOffset,
    hideMenu,
    setHideMenu,
    openMainMenu,
    setOpenMainMenu
  } = useStickyMenu();

  useClickOutside(headerRef, () => {
    setOpenMainMenu(false);
    setSearchIsOpen(false);
  });
  useEscape(() => {
    setOpenMainMenu(false);
    setSearchIsOpen(false);
  });

  const onSearchButtonClick = () => {
    setOpenMainMenu(false);
    setSearchIsOpen(prev => !prev);
  };

  const handleOnMenuToggleClick = () => {
    if (typeof document === 'undefined') return;
    setOpenMainMenu(prev => {
      if (prev) {
        document.body.style.overflow = 'auto';
      } else {
        document.body.style.overflow = 'hidden';
      }
      return !prev;
    });
    setSearchIsOpen(false);
  };

  const handleOnHeaderClick = targetHeader => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = 'auto';
    if (!targetHeader) return;

    searchIsOpen && setSearchIsOpen(false);

    if (currentActiveGroup === targetHeader) {
      setOpenMainMenu(false);
    }
    setCurrentActiveGroup(targetHeader);
    !openMainMenu && setOpenMainMenu(true);
  };

  const handleCloseMenu = () => {
    setOpenMainMenu(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'auto';
    }
  };

  useEffect(() => {
    clearTimeout(timeoutRef.current);

    if (!openMainMenu) {
      timeoutRef.current = setTimeout(() => setCurrentActiveGroup(''), 300);
    }
  }, [openMainMenu]);

  useEffect(() => {
    const resetMenu = () => {
      setOpenMainMenu(false);
      if (typeof document === 'undefined') return;
      document.body.style.overflow = 'auto';
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', resetMenu);

      return () => {
        window.removeEventListener('resize', resetMenu);
      };
    }
  }, []);

  return (
    <header
      id="id-header-hotjar"
      className={cn('header', {
        'header--hide-menu': hideMenu && !(openMainMenu || searchIsOpen)
      })}
      ref={headerRef}
    >
      <div
        className={cn('header__background-shadow', {
          'header__background-shadow--active': openMainMenu || searchIsOpen
        })}
      />
      {globalWarningBlock && (
        <GlobalWarningBlock
          {...globalWarningBlock}
          changeCallback={calculateOffset}
        />
      )}
      <div id="header-main-content" className="header__container">
        <div className="header__content">
          <div className="header__logo-container">
            <a
              aria-label={linkToHomeText}
              href={linkToHomeUrl}
              className={cn('header__logo', {
                'header__logo--search-active': searchIsOpen
              })}
            >
              <Logo theme={Logo.themes.menu} />
            </a>
          </div>
          <div className="header__menu-headers-container">
            <MenuHeaders
              headers={menu?.groups.map(group => group.title)}
              onClick={handleOnHeaderClick}
              activeHeader={currentActiveGroup}
            />
          </div>
          <div className="header__action-buttons">
            <HeaderToggleButton
              className="header__toggle-button-search"
              onClick={onSearchButtonClick}
              isActive={searchIsOpen}
              activeIconName="x"
              nonActiveIconName="search"
              label={searchToggleLabel}
              text={searchToggleText}
            />
            <HeaderToggleButton
              className="header__toggle-button-menu"
              onClick={handleOnMenuToggleClick}
              isActive={openMainMenu}
              activeIconName="x"
              nonActiveIconName="hamburger"
              label={menuToggleLabel}
            />
            <div className="header__login-links">
              {menu?.loginLink && <LoginLink {...menu.loginLink} />}
            </div>
          </div>
        </div>
      </div>
      {search && (
        <div
          className={cn('header__search', {
            'header__search--open': searchIsOpen
          })}
        >
          <TabTrapper isActive={searchIsOpen}>
            <Collapse isOpen={searchIsOpen}>
              <div className="header__search-wrapper">
                <Search
                  shouldFocusInput={true}
                  searchIsOpen={searchIsOpen}
                  theme={Search.themes.menu}
                  {...search}
                />
              </div>
            </Collapse>
          </TabTrapper>
        </div>
      )}
      <div
        className={cn('header__menu', {
          'header__menu--open': openMainMenu
        })}
      >
        <TabTrapper isActive={openMainMenu}>
          <Collapse isOpen={openMainMenu}>
            <Menu
              activeGroup={currentActiveGroup}
              handleCloseMenu={handleCloseMenu}
              {...menu}
            />
          </Collapse>
        </TabTrapper>
      </div>
      {housingProjectMenu && (
        <HousingProjectMenu
          {...housingProjectMenu}
          onToggle={() => {
            setHideMenu(false);
          }}
        />
      )}
    </header>
  );
};

Header.propTypes = {
  linkToHomeUrl: PropTypes.string,
  linkToHomeText: PropTypes.string,
  menuToggleLabel: PropTypes.string,
  searchToggleLabel: PropTypes.string,
  searchToggleText: PropTypes.string,
  search: PropTypes.exact(Search.propTypes),
  menu: PropTypes.exact(Menu.propTypes),
  housingProjectMenu: PropTypes.exact(HousingProjectMenu.propTypes),
  globalWarningBlock: PropTypes.exact(GlobalWarningBlock.propTypes)
};

Header.defaultProps = {
  linkToHomeUrl: '/',
  menuToggleLabel: 'Vis meny',
  searchToggleLabel: 'Vis søk',
  searchToggleText: 'Søk'
};

Header.propTypesMeta = {
  isOpen: 'exclude'
};

export default Header;
